import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, Select, Skeleton } from "@sagot/ui";
import { useState } from "react";
import { useDebounced } from "../../../lib/slug";
import { CONTACT_SEARCH_MIN_CHARS, TICKET_SEARCH_DEBOUNCE_MS } from "./ticket-constants";
import type { NewTicketDraft, Problems } from "./new-ticket-form";
import type { ContactOut } from "./ticket-types";
import type { Option } from "./ticket-filters-popover";
import { useContactSearch } from "./use-new-ticket";

interface PickerProps {
  draft: NewTicketDraft;
  problems: Problems;
  organisations: readonly Option[] | null;
  onChange: (patch: Partial<NewTicketDraft>) => void;
}

function Results({ term, onPick }: { term: string; onPick: (contact: ContactOut) => void }) {
  const { t } = useTranslation();
  const search = useContactSearch(term);
  if (term.length < CONTACT_SEARCH_MIN_CHARS) return null;
  if (search.isPending) return <Skeleton className="h-10 w-full" />;
  if (search.isError) return <p className="text-xs text-fg-muted">{t("service.tickets.new.contactError")}</p>;
  if (search.data.items.length === 0) return <p className="text-xs text-fg-muted">{t("service.tickets.new.contactNone")}</p>;
  return (
    <ul aria-label={t("service.tickets.new.contactResults")} className="max-h-44 divide-y divide-line overflow-y-auto rounded-md border border-line">
      {search.data.items.map((contact) => (
        <li key={contact.id}>
          <button type="button" className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-sunken focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary" onClick={() => { onPick(contact); }}>
            <span className="font-semibold">{contact.name}</span>
            <span className="text-xs text-fg-muted">{[contact.organisation?.name, contact.email].filter(Boolean).join(" · ")}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ChosenContact({ contact, onClear }: { contact: ContactOut; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-surface-2 px-3 py-2" data-testid="chosen-contact">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">{contact.name}</p>
        <p className="truncate text-xs text-fg-muted">{[contact.organisation?.name ?? t("service.tickets.noOrganisation"), contact.email].filter(Boolean).join(" · ")}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={onClear}>
        {t("service.tickets.new.changeContact")}
      </Button>
    </div>
  );
}

/** Who the ticket is for: search an existing contact, or add a walk-in as a new one (name, optional e-mail, optional organisation). */
export function RequesterPicker({ draft, problems, organisations, onChange }: PickerProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term.trim(), TICKET_SEARCH_DEBOUNCE_MS);
  const message = (key: "requester" | "newName" | "newEmail") => {
    const problem = problems[key];
    return problem ? t(problem.key, problem.values) : undefined;
  };

  if (draft.requesterMode === "new") {
    return (
      <div className="space-y-3 rounded-md border border-line p-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("service.tickets.new.newName")} error={message("newName")}>
            <Input value={draft.newName} autoComplete="off" required onChange={(event) => { onChange({ newName: event.target.value }); }} />
          </Field>
          <Field label={t("service.tickets.new.newEmail")} hint={t("service.tickets.new.newEmailHint")} error={message("newEmail")}>
            <Input type="email" value={draft.newEmail} autoComplete="off" onChange={(event) => { onChange({ newEmail: event.target.value }); }} />
          </Field>
        </div>
        {organisations ? (
          <Field label={t("service.tickets.new.organisation")}>
            <Select value={draft.organisationId} onChange={(event) => { onChange({ organisationId: event.target.value }); }}>
              <option value="">{t("service.tickets.noOrganisation")}</option>
              {organisations.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Button variant="ghost" size="sm" onClick={() => { onChange({ requesterMode: "existing" }); }}>
          {t("service.tickets.new.useExisting")}
        </Button>
      </div>
    );
  }

  if (draft.contact) {
    return <ChosenContact contact={draft.contact} onClear={() => { onChange({ contact: null }); }} />;
  }
  return (
    <div className="space-y-2">
      <Field label={t("service.tickets.new.requester")} hint={t("service.tickets.new.requesterHint")} error={message("requester")}>
        <div className="relative">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
          <Input className="pl-9" type="search" value={term} autoComplete="off" placeholder={t("service.tickets.new.requesterPlaceholder")} required onChange={(event) => { setTerm(event.target.value); }} />
        </div>
      </Field>
      <Results term={debounced} onPick={(contact) => { onChange({ contact }); }} />
      <Button variant="ghost" size="sm" onClick={() => { onChange({ requesterMode: "new" }); }}>
        <Icon name="user-plus" />
        {t("service.tickets.new.addNew")}
      </Button>
    </div>
  );
}
